// react
import { type ReactNode, useCallback, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

// material ui
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
} from '@mui/x-data-grid-premium';

// api
import type {
  GetAlarmDto,
  IntAssociation,
} from '@/api/generated/recalc/endpoints/v2/alarms';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import AddressText from '@/components/ui/AddressText';
import AlarmsNoRowsOverlay from '@/components/ui/datagrid/AlarmsNoRowsOverlay';

// feature-components
import AlarmStateSelect from '@/features/alarm-hub/components/AlarmStateSelect';
import {
  formatCost,
  formatQuantity,
  getAlarmImpact,
  parseAlarmMetaData,
} from '@/features/energyxplorer/components/alarms/alarmImpact';
import EnergyXplorerAlarmDetailDialog from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmDetailDialog';
import EnergyXplorerAlarmStateActions from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmStateActions';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import useSearchParams from '@/hooks/useStableSearchParams';
// utils
import formatDateDanish from '@/utils/formatDateDanish';

const OPEN_ALARM_QUERY_PARAM = 'alarmId';
const FAVORITES_STORAGE_KEY_PREFIX = 'energyxplorer_alarm_favorites';

const isUnresolvedAlarm = (alarm: GetAlarmDto) =>
  (alarm.state ?? 'Unresolved') === 'Unresolved';

const fmtPercent = (value: number | null, digits = 1) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(digits)}%`
    : null;

const getBaseloadDeviationPercentage = (alarm: GetAlarmDto) => {
  if (
    alarm.alarmType !== 'ElectricitySystem' ||
    alarm.alarmSubtype !== 'BaseloadDeviation'
  ) {
    return null;
  }

  const metaData = parseAlarmMetaData(alarm.metaData);

  if (!metaData || typeof metaData !== 'object') {
    return null;
  }

  const firstAlarmDetail = Array.isArray(
    (metaData as { alarm_details?: unknown[] }).alarm_details,
  )
    ? (
        metaData as { alarm_details?: Array<{ alarm_result?: unknown }> }
      ).alarm_details?.find(
        (detail) =>
          typeof detail?.alarm_result === 'object' &&
          detail.alarm_result !== null,
      )
    : null;

  const alarmResult =
    firstAlarmDetail && typeof firstAlarmDetail.alarm_result === 'object'
      ? (firstAlarmDetail.alarm_result as {
          compared_value?: unknown;
          lower_limit?: unknown;
          upper_limit?: unknown;
        })
      : null;

  const compared =
    typeof alarmResult?.compared_value === 'number'
      ? alarmResult.compared_value
      : null;
  const lower =
    typeof alarmResult?.lower_limit === 'number'
      ? alarmResult.lower_limit
      : null;
  const upper =
    typeof alarmResult?.upper_limit === 'number'
      ? alarmResult.upper_limit
      : null;
  const normalMean =
    lower != null && upper != null ? (lower + upper) / 2 : null;

  return compared != null && normalMean != null && normalMean !== 0
    ? ((compared - normalMean) / normalMean) * 100
    : null;
};

const getBaseloadDeviationColumnLabel = (label: string) =>
  label.endsWith(':') ? label.slice(0, -1) : label;

type AlarmFavoriteMap = Record<string, boolean>;

type AlarmRow = {
  id: number | string;
  alarm: GetAlarmDto;
  favorite: boolean;
};

const getAssociationNames = (associations?: IntAssociation[] | null) =>
  associations
    ?.filter((association): association is IntAssociation & { name: string } =>
      Boolean(association.name),
    )
    .map((association) => association.name) ?? [];

const DeepDiveAddressText = ({ energyGroupId }: { energyGroupId: number }) => {
  const { data: energyGroup } = useGetEnergyGroupQuery({
    groupId: energyGroupId,
  });

  const rootNode = energyGroup?.energyTreeNodes?.find((node) => !node.parentId);
  const locationId = rootNode?.locationId ?? energyGroup?.locationId;

  return (
    <AddressText
      locationId={locationId}
      variant="caption"
      color="text.secondary"
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
    />
  );
};

interface EnergyXplorerAlarmsGridProps {
  accountId: string | undefined;
  alarms: GetAlarmDto[];
  isLoading: boolean;
  emptyText: string;
  title?: string;
  toolbarActions?: ReactNode;
}

type EnergyXplorerAlarmsGridToolbarProps = {
  title?: string;
  toolbarActions?: ReactNode;
  isFavoritesFiltered: boolean;
  onFavoritesToggle: () => void;
  showPast: boolean;
  onShowPastToggle: () => void;
};

const EnergyXplorerAlarmsGridToolbar = ({
  title,
  toolbarActions,
  isFavoritesFiltered,
  onFavoritesToggle,
  showPast,
  onShowPastToggle,
}: EnergyXplorerAlarmsGridToolbarProps) => {
  const { t } = useTranslation('energy-analysis');

  return (
    <GridToolbarContainer>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        width="100%"
        flexWrap="wrap"
      >
        {title ? (
          <Typography variant="subtitle1" fontWeight={600} ml={1}>
            {title}
          </Typography>
        ) : (
          <Box />
        )}

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button size="small" onClick={onShowPastToggle}>
            {showPast
              ? t('portfolio.deepdive_alarms.actions.show_unresolved')
              : t('portfolio.deepdive_alarms.actions.show_past')}
          </Button>
          <Tooltip title="toolbar.only_favorites">
            <IconButton
              size="small"
              onClick={onFavoritesToggle}
              aria-label="toolbar.only_favorites"
            >
              {isFavoritesFiltered ? (
                <StarRoundedIcon fontSize="small" color="warning" />
              ) : (
                <StarHalfRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {toolbarActions}
        </Box>
      </Box>
    </GridToolbarContainer>
  );
};

const EnergyXplorerAlarmsGrid = ({
  accountId,
  alarms,
  isLoading,
  emptyText,
  title,
  toolbarActions,
}: EnergyXplorerAlarmsGridProps) => {
  const { t } = useTranslation(['energy-analysis', 'alarms', 'common']);
  const localeText = useDatagridLocaleText();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFavoritesFiltered, setIsFavoritesFiltered] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [favorites, setFavorites] = useLocalStorageJson(
    `${FAVORITES_STORAGE_KEY_PREFIX}_${accountId ?? 'unknown'}`,
    {},
  ) as [
    AlarmFavoriteMap,
    React.Dispatch<React.SetStateAction<AlarmFavoriteMap>>,
  ];
  const selectedAlarmIdParam = searchParams.get(OPEN_ALARM_QUERY_PARAM);
  const parsedSelectedAlarmId =
    selectedAlarmIdParam && selectedAlarmIdParam.trim().length > 0
      ? Number(selectedAlarmIdParam)
      : null;
  const selectedAlarmId = Number.isFinite(parsedSelectedAlarmId)
    ? parsedSelectedAlarmId
    : null;
  const dialogKey = `${accountId ?? 'unknown'}-${selectedAlarmId ?? 'closed'}`;

  const shownAlarms = useMemo(
    () =>
      alarms.filter((alarm) =>
        showPast ? !isUnresolvedAlarm(alarm) : isUnresolvedAlarm(alarm),
      ),
    [alarms, showPast],
  );

  const rows = useMemo<AlarmRow[]>(
    () =>
      shownAlarms.map((alarm, index) => ({
        id: alarm.alarmId ?? `alarm-${index}`,
        alarm,
        favorite: Boolean(alarm.alarmId && favorites[String(alarm.alarmId)]),
      })),
    [favorites, shownAlarms],
  );

  const visibleRows = useMemo(
    () => (isFavoritesFiltered ? rows.filter((row) => row.favorite) : rows),
    [isFavoritesFiltered, rows],
  );

  const toggleFavorite = useCallback(
    (
      alarmId: number | undefined,
      event?: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event?.stopPropagation();

      if (!alarmId) {
        return;
      }

      setFavorites((previousFavorites) => {
        const key = String(alarmId);
        const nextFavorites = { ...previousFavorites };

        if (nextFavorites[key]) {
          delete nextFavorites[key];
        } else {
          nextFavorites[key] = true;
        }

        return nextFavorites;
      });
    },
    [setFavorites],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'favorite',
        headerName: '',
        width: 72,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        sortingOrder: ['desc', 'asc', null],
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<AlarmRow>) => {
          const alarmId = params.row.alarm.alarmId;
          const isFavorite = params.row.favorite;

          return (
            <IconButton
              size="small"
              onClick={(event) => toggleFavorite(alarmId, event)}
              aria-label={
                isFavorite
                  ? t('portfolio.deepdive_alarms.actions.remove_favorite', {
                      ns: 'energy-analysis',
                    })
                  : t('portfolio.deepdive_alarms.actions.add_favorite', {
                      ns: 'energy-analysis',
                    })
              }
            >
              {isFavorite ? (
                <StarRoundedIcon fontSize="small" color="warning" />
              ) : (
                <StarBorderRoundedIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: 'scope',
        headerName: t('portfolio.deepdive_alarms.column_headers.scope', {
          ns: 'energy-analysis',
        }),
        flex: 1.2,
        minWidth: 280,
        valueGetter: (_, row) => {
          const deepDives = getAssociationNames(
            row.alarm.associations?.energyGroups,
          );
          const addresses = getAssociationNames(
            row.alarm.associations?.buildings,
          );

          return [...deepDives, ...addresses].join(', ');
        },
        renderCell: (params: GridRenderCellParams<AlarmRow>) => {
          const deepDives = getAssociationNames(
            params.row.alarm.associations?.energyGroups,
          );
          const addresses = getAssociationNames(
            params.row.alarm.associations?.buildings,
          );

          return (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              minWidth={0}
              width="100%"
            >
              {deepDives.length > 0 ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {deepDives.join(', ')}
                </Typography>
              ) : null}

              {params.row.alarm.associations?.energyGroups?.map(
                (energyGroup) =>
                  energyGroup.id != null ? (
                    <DeepDiveAddressText
                      key={energyGroup.id}
                      energyGroupId={energyGroup.id}
                    />
                  ) : null,
              )}

              {addresses.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {addresses.join(', ')}
                </Typography>
              ) : null}

              {deepDives.length === 0 && addresses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('dash', { ns: 'common' })}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'created',
        headerName: t('portfolio.deepdive_alarms.column_headers.created', {
          ns: 'energy-analysis',
        }),
        width: 170,
        valueGetter: (_, row) => row.alarm.created ?? row.alarm.Created ?? null,
        renderCell: (params: GridRenderCellParams<AlarmRow>) => (
          <Typography variant="body2" noWrap>
            {formatDateDanish(String(params.value ?? '')) ||
              t('dash', { ns: 'common' })}
          </Typography>
        ),
      },
      {
        field: 'baseloadDeviationPercentage',
        headerName: getBaseloadDeviationColumnLabel(
          t(
            'type.ElectricitySystem.subtype.BaseloadDeviation.percentage_increase',
            {
              ns: 'alarms',
              defaultValue: 'Percentage increase',
            },
          ),
        ),
        width: 180,
        type: 'number',
        valueGetter: (_, row) => getBaseloadDeviationPercentage(row.alarm),
        renderCell: (params: GridRenderCellParams<AlarmRow>) => {
          const formattedBaseloadDeviationPercentage = fmtPercent(
            typeof params.value === 'number' ? params.value : null,
          );

          return (
            <Typography variant="body2" noWrap>
              {formattedBaseloadDeviationPercentage ??
                t('dash', { ns: 'common' })}
            </Typography>
          );
        },
      },
      {
        field: 'monthlyQuantityDelta',
        headerName: t(
          'portfolio.deepdive_alarms.column_headers.monthly_quantity_delta',
          { ns: 'energy-analysis' },
        ),
        width: 160,
        type: 'number',
        valueGetter: (_, row) =>
          getAlarmImpact(row.alarm.metaData)?.monthlyQuantityDelta ?? null,
        valueFormatter: (value: number | null, row: AlarmRow) =>
          value == null
            ? t('dash', { ns: 'common' })
            : formatQuantity(
                value,
                getAlarmImpact(row.alarm.metaData)?.unit ?? 'kWh',
              ),
      },
      {
        field: 'monthlyCost',
        headerName: t('portfolio.deepdive_alarms.column_headers.monthly_cost', {
          ns: 'energy-analysis',
        }),
        width: 160,
        type: 'number',
        valueGetter: (_, row) =>
          getAlarmImpact(row.alarm.metaData)?.monthlyCost ?? null,
        valueFormatter: (value: number | null) =>
          value == null ? t('dash', { ns: 'common' }) : formatCost(value),
      },
      {
        field: 'actions',
        headerName: t('columns.actions', { ns: 'alarms' }),
        width: 250,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<AlarmRow>) =>
          isUnresolvedAlarm(params.row.alarm) ? (
            <EnergyXplorerAlarmStateActions
              accountId={accountId}
              alarm={params.row.alarm}
            />
          ) : (
            <AlarmStateSelect
              params={{
                row: {
                  state: params.row.alarm.state,
                  alarmId: params.row.alarm.alarmId,
                },
              }}
            />
          ),
      },
    ],
    [accountId, t, toggleFavorite],
  );

  const handleRowClick = (alarm: GetAlarmDto) => {
    if (!accountId || !alarm.alarmId) return;

    setSearchParams((previousParams: URLSearchParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set(OPEN_ALARM_QUERY_PARAM, String(alarm.alarmId));
      return nextParams;
    });
  };

  const handleCloseDialog = useCallback(() => {
    setSearchParams((previousParams: URLSearchParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.delete(OPEN_ALARM_QUERY_PARAM);
      return nextParams;
    });
  }, [setSearchParams]);

  const handleFavoritesToggle = useCallback(() => {
    setIsFavoritesFiltered((currentValue) => !currentValue);
  }, []);

  const handleDialogFavoriteToggle = () =>
    toggleFavorite(selectedAlarmId ?? undefined);

  const ToolbarSlot = () => (
    <EnergyXplorerAlarmsGridToolbar
      title={title}
      toolbarActions={toolbarActions}
      isFavoritesFiltered={isFavoritesFiltered}
      onFavoritesToggle={handleFavoritesToggle}
      showPast={showPast}
      onShowPastToggle={() => setShowPast((current) => !current)}
    />
  );

  return (
    <>
      <Card>
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={400}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <DataGridPremium
            rows={visibleRows}
            columns={columns}
            showToolbar={Boolean(title || toolbarActions || rows.length)}
            hideFooter
            disableRowSelectionOnClick
            localeText={localeText}
            onRowClick={(params) => handleRowClick(params.row.alarm)}
            slots={{
              toolbar: ToolbarSlot,
              noRowsOverlay: () => (
                <AlarmsNoRowsOverlay rows={emptyText} isLoading={false} />
              ),
            }}
            sx={{
              height: 440,
              '& .MuiDataGrid-row': {
                cursor: visibleRows.length ? 'pointer' : 'default',
              },
            }}
          />
        )}
      </Card>

      <EnergyXplorerAlarmDetailDialog
        key={dialogKey}
        open={selectedAlarmId !== null}
        onClose={handleCloseDialog}
        accountId={accountId}
        alarmId={selectedAlarmId}
        isFavorite={Boolean(
          selectedAlarmId && favorites[String(selectedAlarmId)],
        )}
        onToggleFavorite={handleDialogFavoriteToggle}
      />
    </>
  );
};

export default EnergyXplorerAlarmsGrid;
